import { createEmptyLead, defaultValidUntil } from "../leads/utils";
import { normalizeGpuHardware, SERVER_SKU_PROMPT } from "../leads/hardware";
import type {
  AgentConfig,
  BrokerEntry,
  Lead,
  LeadDirection,
} from "../leads/types";

const SYSTEM_PROMPT = `你是大宗物品交易盘口录入助手。把用户粘贴的微信、邮件或聊天记录提取为结构化 JSON。
只输出 JSON，不要 Markdown。格式为 {"leads":[{...}]}，每个独立买方或卖方一条。
字段名必须使用：direction(sell/buy), status(active/draft), category, brand, sku, model, configuration, memory_capacity, quantity(number|null), unit, price(number|null), currency(CNY/USD/USDT), estimated_fee_per_unit(number|null), price_basis, availability(stock/transit/presale/unknown), location, available_at(ISO|null), valid_until(ISO), last_verified_at(ISO), payment_terms, inspection, transaction_steps(string[]), company, contact_name, contact_info, source_channel, owner, notes, ai_confidence(0到1)。
规则：不臆造价格、数量、公司、联系方式或条件；价格面议用 null；数量未知用 null；多个对手方拆成多条；无法识别 direction 或 model 时 status=draft；现货默认 48 小时有效，求购默认 72 小时；相对时间基于用户提供的当前时间换算；交易流程按顺序拆入 transaction_steps；original raw text 由客户端保留。
B300/H200 是 GPU 平台，不是服务器 SKU。model 只写平台与内存版本（如 B300 3T）；sku 只能填写原文明确出现的官方服务器型号，未出现则为空。不要把 8 卡、8 GPU、整机、HGX/DGX 或固定机箱参数重复写入 model/configuration；configuration 只保留风冷、液冷等交易变量。官方目录：${SERVER_SKU_PROMPT}。`;

function stripJsonFence(content: string): string {
  return content
    .trim()
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```$/, "");
}

function parseNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  const raw = String(value).replaceAll(",", "");
  const match = raw.match(/-?\d+(?:\.\d+)?/);
  if (!match) return null;
  let result = Number(match[0]);
  if (/万|w\b/i.test(raw)) result *= 10_000;
  return Number.isFinite(result) ? result : null;
}

function normalizeCurrency(value: unknown): string {
  const raw = String(value ?? "").toUpperCase();
  if (raw.includes("USDT")) return "USDT";
  if (raw.includes("USD") || raw.includes("美元") || raw.includes("美金"))
    return "USD";
  return "CNY";
}

function extractContactName(text: string): string {
  return (
    text.match(
      /(?:货主|貨主|买主|買主)\s*[:：]?\s*([\p{Script=Han}A-Za-z0-9_-]{1,30})/u,
    )?.[1] ??
    text.match(
      /([\p{Script=Han}A-Za-z0-9_-]{1,30})\s*(?:货主|貨主|买主|買主)/u,
    )?.[1] ??
    ""
  );
}

function normalizeBrokers(value: unknown): BrokerEntry[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) => {
      if (typeof entry === "string") {
        const name = entry.trim();
        return name ? { name, contact_info: "" } : null;
      }
      if (entry && typeof entry === "object") {
        const record = entry as Record<string, unknown>;
        const name = String(record.name ?? "").trim();
        return name
          ? { name, contact_info: String(record.contact_info ?? "").trim() }
          : null;
      }
      return null;
    })
    .filter((entry): entry is BrokerEntry => Boolean(entry));
}

export function normalizeInquiries(value: unknown): Lead["inquiries"] {
  if (!Array.isArray(value)) return [];
  const statuses = ["talking", "offered", "won", "lost"];
  return value
    .map((entry) => {
      if (typeof entry === "string") {
        const buyer_name = entry.trim();
        return buyer_name
          ? {
              id: crypto.randomUUID(),
              buyer_name,
              contact_info: "",
              offer: null,
              status: "talking" as const,
              note: "",
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            }
          : null;
      }
      if (entry && typeof entry === "object") {
        const record = entry as Record<string, unknown>;
        const buyer_name = String(
          record.buyer_name ?? record.name ?? "",
        ).trim();
        if (!buyer_name) return null;
        const rawOffer = Number(record.offer);
        const existingId = String(record.id ?? "").trim();
        return {
          id: existingId || crypto.randomUUID(),
          buyer_name,
          contact_info: String(record.contact_info ?? "").trim(),
          offer: Number.isFinite(rawOffer) ? rawOffer : null,
          status: statuses.includes(String(record.status))
            ? (String(record.status) as Lead["inquiries"][number]["status"])
            : "talking",
          note: String(record.note ?? "").trim(),
          created_at:
            record.created_at &&
            !Number.isNaN(new Date(String(record.created_at)).getTime())
              ? new Date(String(record.created_at)).toISOString()
              : new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
      }
      return null;
    })
    .filter((entry): entry is Lead["inquiries"][number] => Boolean(entry));
}

export function normalizeParsedLead(
  raw: Record<string, unknown>,
  originalText: string,
  owner: string,
): Lead {
  const direction: LeadDirection =
    String(raw.direction).toLowerCase() === "buy" ? "buy" : "sell";
  const base = createEmptyLead(direction, owner);
  const sku = String(raw.sku ?? "").trim();
  const memoryCapacity =
    String(raw.memory_capacity ?? "").trim() || detectMemory(originalText);
  const model =
    String(raw.model ?? "").trim() ||
    [sku, memoryCapacity].filter(Boolean).join(" ");
  const rawContact = String(raw.contact_name ?? "").trim();
  const invalidContact =
    !rawContact ||
    rawContact === owner ||
    /^[0-9a-f]{8}-[0-9a-f-]{27,}$/i.test(rawContact);
  const validUntil =
    raw.valid_until &&
    !Number.isNaN(new Date(String(raw.valid_until)).getTime())
      ? new Date(String(raw.valid_until)).toISOString()
      : defaultValidUntil(direction);
  const record: Lead = {
    ...base,
    direction,
    status: model ? "active" : "draft",
    category: String(raw.category ?? base.category),
    brand: String(raw.brand ?? ""),
    sku,
    model,
    configuration: String(raw.configuration ?? ""),
    memory_capacity: memoryCapacity,
    quantity: parseNumber(raw.quantity),
    unit: String(raw.unit ?? base.unit),
    price: parseNumber(raw.price),
    currency: /USDT/i.test(originalText)
      ? "USDT"
      : normalizeCurrency(raw.currency ?? originalText),
    estimated_fee_per_unit: parseNumber(raw.estimated_fee_per_unit),
    price_basis: String(raw.price_basis ?? ""),
    availability: ["stock", "transit", "presale", "unknown"].includes(
      String(raw.availability),
    )
      ? (raw.availability as Lead["availability"])
      : "unknown",
    location: String(raw.location ?? ""),
    available_at:
      raw.available_at &&
      !Number.isNaN(new Date(String(raw.available_at)).getTime())
        ? new Date(String(raw.available_at)).toISOString()
        : null,
    valid_until: validUntil,
    last_verified_at: new Date().toISOString(),
    payment_terms: String(raw.payment_terms ?? ""),
    inspection: String(raw.inspection ?? ""),
    transaction_steps: Array.isArray(raw.transaction_steps)
      ? raw.transaction_steps
          .map(String)
          .map((step) => step.trim())
          .filter(Boolean)
      : [],
    company: String(raw.company ?? ""),
    contact_name: invalidContact
      ? extractContactName(originalText)
      : rawContact,
    contact_info: String(raw.contact_info ?? ""),
    seller_brokers: normalizeBrokers(raw.seller_brokers),
    buyer_brokers: normalizeBrokers(raw.buyer_brokers),
    inquiries: normalizeInquiries(raw.inquiries),
    source_channel: String(raw.source_channel ?? "AI 粘贴"),
    raw_text: originalText,
    owner,
    notes: String(raw.notes ?? ""),
    note_images: Array.isArray(raw.note_images)
      ? raw.note_images.filter(
          (image): image is string =>
            typeof image === "string" && image.startsWith("data:image/"),
        )
      : [],
    ai_confidence: parseNumber(raw.ai_confidence),
    created_by: owner,
  };
  return { ...record, ...normalizeGpuHardware(record) };
}

export async function parseWithAgent(
  text: string,
  config: AgentConfig,
  owner: string,
): Promise<Lead[]> {
  const endpoint = `${config.baseUrl.replace(/\/$/, "")}/chat/completions`;
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 45_000);
  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${config.apiKey}`,
      },
      body: JSON.stringify({
        model: config.model,
        temperature: 0.1,
        messages: [
          { role: "system", content: SYSTEM_PROMPT },
          {
            role: "user",
            content: `当前时间：${new Date().toISOString()}\n默认负责人：${owner}\n\n待解析原文：\n${text}`,
          },
        ],
      }),
      signal: controller.signal,
    });
    if (!response.ok) {
      const detail = await response.text();
      throw new Error(`接口返回 ${response.status}：${detail.slice(0, 240)}`);
    }
    const payload = await response.json();
    const content = payload?.choices?.[0]?.message?.content;
    if (typeof content !== "string")
      throw new Error("接口未返回可解析的消息内容");
    const parsed = JSON.parse(stripJsonFence(content));
    const rows = Array.isArray(parsed) ? parsed : parsed.leads;
    if (!Array.isArray(rows) || rows.length === 0)
      throw new Error("没有识别到买卖盘，请补充型号和买卖方向");
    return rows.map((row) => normalizeParsedLead(row, text, owner));
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError")
      throw new Error("解析超时，请重试");
    if (error instanceof SyntaxError)
      throw new Error("模型返回了非标准 JSON，请重试");
    throw error;
  } finally {
    window.clearTimeout(timer);
  }
}

function detectDirection(text: string): LeadDirection {
  return /求购|采购|需要|收货|买入|\bbuy\b|\blooking for\b/i.test(text)
    ? "buy"
    : "sell";
}

function detectSku(text: string): string {
  return text.match(/GB300|B300|H200|H100|RTX\s*\d+/i)?.[0].toUpperCase() ?? "";
}

function detectMemory(text: string): string {
  return (
    text
      .match(/(?<![\d.])\d+(?:\.\d+)?\s*T\b/i)?.[0]
      .replace(/\s+/g, "")
      .toUpperCase() ?? ""
  );
}

function detectModel(text: string): string {
  const sku = detectSku(text);
  const memory = detectMemory(text);
  if (sku && memory) return `${sku} ${memory}`;
  const explicit = text.match(
    /(?:型号|品名|标的)[:：]\s*([^，,。；;\n]+)/,
  )?.[1];
  if (explicit) return explicit.trim();
  const hardware = text.match(
    /(?:HGX|DGX|GB300|B300|H200|H100|RTX\s*\d+)[^，,。；;\n]{0,28}/i,
  )?.[0];
  if (hardware) return hardware.trim();
  return (
    text
      .split(/[，,。；;\n]/)[0]
      ?.slice(0, 40)
      .trim() ?? ""
  );
}

export function extractTransactionSteps(text: string): string[] {
  const steps: string[] = [];
  for (const rawLine of text.split("\n")) {
    const line = rawLine.replace(/^\s*(?:流程|流程[-—:：]*)\s*/i, "").trim();
    if (
      !line ||
      !/验资|驗資|验货|驗貨|付款|支付|定金|合同|签约|簽約|交割|提货|提貨/.test(
        line,
      )
    )
      continue;
    const paymentAt = line.search(/付款|支付/);
    if (paymentAt > 0 && /验货|驗貨/.test(line.slice(0, paymentAt))) {
      steps.push(line.slice(0, paymentAt).replace(/[，,；;、\s]+$/, ""));
      steps.push(line.slice(paymentAt).trim());
    } else {
      steps.push(line);
    }
  }
  return steps.filter((step, index) => step && steps.indexOf(step) === index);
}

function localParseOne(
  segment: string,
  originalText: string,
  owner: string,
): Lead {
  const direction = detectDirection(segment);
  const lead = createEmptyLead(direction, owner);
  const quantity = segment.match(
    /(\d+(?:\.\d+)?)\s*(台|套|吨|千克|公斤|kg|件|卡)/i,
  );
  const price = segment.match(
    /(?:单价|价格|报价)?\s*(\d+(?:\.\d+)?)\s*(万|萬|w)?\s*(元|人民币|美元|美金|USDT)?\s*(?:\/|每)\s*(台|套|吨|千克|公斤|kg|件|卡)/i,
  );
  const phone = segment.match(/(?:\+?\d[\d\s-]{6,}\d)/)?.[0];
  const locations =
    segment.match(
      /马来西亚|馬來西亞|槟城|檳城|香港|深圳|上海|广州|北京|宁波|天津|葵涌|荃湾|元朗/gu,
    ) ?? [];
  const fee = segment.match(
    /(?:预估|預估)?居(?:间|間)费(?:\s*\/\s*台)?\s*[:：]?\s*(\d+(?:\.\d+)?\s*(?:万|萬|w)?)/i,
  )?.[1];
  const priceLine = segment
    .split("\n")
    .find((line) => /价格|價格|报价|報價/.test(line));
  lead.sku = detectSku(segment);
  lead.memory_capacity = detectMemory(segment);
  lead.model = detectModel(segment);
  lead.brand =
    segment.match(/技嘉|超微|Supermicro|Dell|NVIDIA|华硕|華碩/i)?.[0] ?? "";
  lead.configuration = lead.memory_capacity
    ? `${lead.memory_capacity} 版本`
    : "";
  lead.status = lead.model ? "active" : "draft";
  lead.quantity = quantity ? Number(quantity[1]) : null;
  lead.unit = quantity?.[2] ?? "台";
  lead.price = price ? Number(price[1]) * (price[2] ? 10_000 : 1) : null;
  lead.currency = normalizeCurrency(
    price?.[3] ?? segment.match(/USDT|美元|美金|人民币|元/)?.[0],
  );
  lead.estimated_fee_per_unit = parseNumber(fee);
  lead.price_basis =
    !price && priceLine
      ? `${priceLine.trim()}（总价 / 单价待确认）`
      : lead.price_basis;
  lead.location = [...new Set(locations)].join(" · ");
  lead.contact_name =
    segment.match(/[\p{Script=Han}A-Za-z]+\s*(?:货主|貨主|买主|買主)/u)?.[0] ??
    "";
  lead.contact_info = phone ?? "";
  lead.availability = /现货|現貨/.test(segment) ? "stock" : "unknown";
  lead.transaction_steps = extractTransactionSteps(segment);
  lead.raw_text = originalText;
  lead.source_channel = "本地规则解析";
  lead.ai_confidence = 0.55;
  lead.notes = "本地规则结果，请重点核对型号、价格和交割条件。";
  return lead;
}

export function parseLocally(text: string, owner: string): Lead[] {
  let segments = text
    .split(/\n\s*\n/)
    .map((item) => item.trim())
    .filter(Boolean);
  if (segments.length <= 1) {
    const lines = text
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean);
    const directional = lines.filter((line) =>
      /求购|采购|出售|现货|有货|供应|\bbuy\b|\bsell\b/i.test(line),
    );
    if (directional.length > 1) segments = directional;
  }
  return segments.map((segment) => localParseOne(segment, text, owner));
}
